/**
 * Silo API Client - Main Module Composition
 * 
 * This is the main entry point that composes all API modules and provides
 * backward compatibility for the existing monolithic silo-api.ts implementation.
 * 
 * CRITICAL: Maintains 100% backward compatibility for all existing public methods.
 */

// Import all modules
import { CrmApi } from './crm'
import { InventoryApi } from './inventory'
import { OrdersApi } from './orders'
import { AuthApi } from './auth'
import { UsersApi } from './users'
import { AnalyticsApi } from './analytics'
import { SystemApi } from './system'
import { PartnerApi } from './partner'
import { ActivitiesApi } from './activities'
import { QuickBooksApi } from './quickbooks'
import { HarvestApi } from './harvest'
import { TransfersApi } from './transfers'
import { ErrorHandler } from './errors'

// Import fetchx for HTTP requests
import { fetchx } from '../fetchx'

// Import types
import type {
  RequestFunction,
  FilterParams,
  PaginatedResponse,
  // User types
  User,
  UserCreateInput,
  UserUpdateInput,
  UserProfile,
  ProfileUpdateInput,
  // Contact types
  Contact,
  ContactCreateInput,
  ContactUpdateInput,
  ContactNote,
  ContactNoteCreateInput,
  ContactBusiness,
  ContactBusinessCreateInput,
  ContactBusinessUpdateInput,
  ContactSubmission,
  ContactSubmissionUpdateInput,
  // Product types
  InventoryItem,
  ProductCreateInput,
  ProductUpdateInput,
  ProductType,
  ProductDetail,
  Category,
  ProductTerpene,
  Terpene,
  ProductCannabinoid,
  Cannabinoid,
  // Order types
  Order,
  OrderCreateInput,
  OrderUpdateInput,
  OrderStatus,
  OrderItem,
  PaymentMethod,
  Shipping,
  // Address types
  Address,
  AddressCreateInput,
  AddressUpdateInput,
  // Dashboard types
  AdminDashboardStats,
  DashboardSummary,
  // Notification types
  NotificationPreferences,
  NotificationPreferencesUpdateInput,
  // New sync types
  OrderTimeline,
  OrderHistory,
  ContactInteractionHistory,
  ContactNotificationPreferences,
  ProductRecommendation,
  BatchActivity,
  ProductionDashboard,
  MaterialTransfer,
  MaterialTransferTracking,
  // Material Transfer types
  MaterialTransferItem,
  CreateTransferInput,
  UpdateTransferInput,
  AddPalletInput,
  GeneratePDFResponse,
  PDFResponse,
  TransferStats,
  AvailablePalletsResponse,
  PaginatedTransfers,
  TransferNote,
} from './types'

// Request options interface
interface RequestOptions {
  method?: string
  params?: Record<string, any>
  body?: string
  headers?: Record<string, string>
}

export class SiloApiClient {
  private baseURL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000'
  private errorHandler = new ErrorHandler()
  
  // Compose modules
  public crm: CrmApi
  public inventory: InventoryApi
  public orders: OrdersApi
  public auth: AuthApi
  public users: UsersApi
  public analytics: AnalyticsApi
  public system: SystemApi
  public activities: ActivitiesApi
  public quickbooks: QuickBooksApi
  public harvest: HarvestApi
  public transfers: TransfersApi

  constructor() {
    const makeRequest = this.makeRequest.bind(this)
    this.crm = new CrmApi(makeRequest)
    this.inventory = new InventoryApi(makeRequest)
    this.orders = new OrdersApi(makeRequest)
    this.auth = new AuthApi(makeRequest)
    this.users = new UsersApi(makeRequest)
    this.analytics = new AnalyticsApi(makeRequest)
    this.system = new SystemApi(makeRequest)
    this.activities = new ActivitiesApi(makeRequest)
    this.quickbooks = new QuickBooksApi(makeRequest)
    this.harvest = new HarvestApi(makeRequest)
    this.transfers = new TransfersApi(makeRequest)
  }

  // ============================================================================
  // CORE HTTP REQUEST HANDLER
  // ============================================================================

  private async makeRequest<T>(url: string, options: RequestOptions = {}): Promise<T> {
    // For client-side requests, fetchx expects relative paths starting with /
    // It will handle the base URL internally based on environment
    const requestPath = url.startsWith('/') ? url : `/${url}`
    
    let config: RequestInit = {
      credentials: 'include', // CRITICAL: Always include cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      }
    }

    // Handle HTTP method
    if (options.method) {
      config.method = options.method
    }

    // Handle request body
    if (options.body) {
      config.body = options.body
    }

    // Handle URL parameters
    let finalUrl = requestPath
    if (options.params) {
      // Filter out undefined and null values
      const cleanParams = Object.entries(options.params).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value
        }
        return acc
      }, {} as any)
      
      if (Object.keys(cleanParams).length > 0) {
        const params = new URLSearchParams(cleanParams)
        finalUrl += (requestPath.includes('?') ? '&' : '?') + params.toString()
      }
    }
    
    // Use enhanced fetchx with auto-refresh
    // fetchx will determine the correct base URL based on environment (client vs server)
    const response = await fetchx(finalUrl, config)
    return ErrorHandler.handleResponse(response)
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY METHODS - PRODUCTS/INVENTORY
  // ============================================================================

  async getProducts(params?: FilterParams): Promise<PaginatedResponse<InventoryItem>> {
    return this.inventory.getProducts(params)
  }

  async getProduct(id: number): Promise<InventoryItem> {
    return this.inventory.getProduct(id)
  }

  async getProductDetail(id: number): Promise<ProductDetail> {
    return this.inventory.getProductDetail(id)
  }

  async createProduct(data: ProductCreateInput): Promise<InventoryItem> {
    return this.inventory.createProduct(data)
  }

  async updateProduct(id: number, data: ProductUpdateInput): Promise<InventoryItem> {
    return this.inventory.updateProduct(id, data)
  }

  async deleteProduct(id: number): Promise<void> {
    return this.inventory.deleteProduct(id)
  }

  async bulkArchiveProducts(ids: number[]): Promise<void> {
    return this.inventory.bulkArchiveProducts(ids)
  }

  async updateProductQuantity(id: number, quantity: number): Promise<InventoryItem> {
    return this.inventory.updateProductQuantity(id, quantity)
  }

  async getProductTypes(): Promise<ProductType[]> {
    return this.inventory.getProductTypes()
  }

  async getCategories(params?: FilterParams): Promise<PaginatedResponse<Category>> {
    return this.inventory.getCategories(params)
  }

  async createCategory(data: { name: string; description?: string }): Promise<Category> {
    return this.inventory.createCategory(data)
  }

  async updateCategory(id: number, data: { name: string; description?: string }): Promise<Category> {
    return this.inventory.updateCategory(id, data)
  }

  async deleteCategory(id: number): Promise<void> {
    return this.inventory.deleteCategory(id)
  }

  // Inventory-specific aliases
  async getInventoryItems(params?: FilterParams): Promise<PaginatedResponse<InventoryItem>> {
    return this.inventory.getInventoryItems(params)
  }

  async getInventoryItem(id: number): Promise<InventoryItem> {
    return this.inventory.getInventoryItem(id)
  }

  async createInventoryItem(data: ProductCreateInput): Promise<InventoryItem> {
    return this.inventory.createInventoryItem(data)
  }

  async updateInventoryItem(id: number, data: ProductUpdateInput): Promise<InventoryItem> {
    return this.inventory.updateInventoryItem(id, data)
  }

  async deleteInventoryItem(id: number): Promise<void> {
    return this.inventory.deleteInventoryItem(id)
  }

  async updateInventoryQuantity(id: number, quantity: number, available_quantity?: number): Promise<InventoryItem> {
    return this.inventory.updateInventoryQuantity(id, quantity, available_quantity)
  }

  async getLowStockItems(threshold: number = 10): Promise<InventoryItem[]> {
    return this.inventory.getLowStockItems(threshold)
  }

  async bulkArchiveInventory(inventoryIds: number[]): Promise<{ updated_count: number }> {
    return this.inventory.bulkArchiveInventory(inventoryIds)
  }

  // Product Terpenes Management
  // NOTE: These endpoints don't exist - terpenes are extracted from product data in the store
  // async getAvailableTerpenes(): Promise<Terpene[]> {
  //   return this.inventory.getAvailableTerpenes()
  // }

  async addProductTerpene(productId: number, data: { terpene_id: number; percentage: string }): Promise<ProductTerpene> {
    return this.inventory.addProductTerpene(productId, data)
  }

  async updateProductTerpene(productId: number, terpeneId: number, data: { percentage: string }): Promise<ProductTerpene> {
    return this.inventory.updateProductTerpene(productId, terpeneId, data)
  }

  async deleteProductTerpene(productId: number, terpeneId: number): Promise<void> {
    return this.inventory.deleteProductTerpene(productId, terpeneId)
  }

  // Product Cannabinoids Management
  // NOTE: These endpoints don't exist - cannabinoids are extracted from product data in the store
  // async getAvailableCannabinoids(): Promise<Cannabinoid[]> {
  //   return this.inventory.getAvailableCannabinoids()
  // }

  async addProductCannabinoid(productId: number, data: { cannabinoid_id: number; percentage: string }): Promise<ProductCannabinoid> {
    return this.inventory.addProductCannabinoid(productId, data)
  }

  async updateProductCannabinoid(productId: number, cannabinoidId: number, data: { percentage: string }): Promise<ProductCannabinoid> {
    return this.inventory.updateProductCannabinoid(productId, cannabinoidId, data)
  }

  async deleteProductCannabinoid(productId: number, cannabinoidId: number): Promise<void> {
    return this.inventory.deleteProductCannabinoid(productId, cannabinoidId)
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY METHODS - CONTACTS/CRM
  // ============================================================================

  async getContacts(params?: FilterParams): Promise<PaginatedResponse<Contact>> {
    return this.crm.getContacts(params)
  }

  async getContact(id: number): Promise<Contact> {
    return this.crm.getContact(id)
  }

  async createContact(data: ContactCreateInput): Promise<Contact> {
    return this.crm.createContact(data)
  }

  async updateContact(id: number, data: ContactUpdateInput): Promise<Contact> {
    return this.crm.updateContact(id, data)
  }

  async deleteContact(id: number): Promise<void> {
    return this.crm.deleteContact(id)
  }

  async bulkUpdateContactStatus(ids: number[], status: string): Promise<void> {
    return this.crm.bulkUpdateContactStatus(ids, status)
  }

  async getContactStatuses(): Promise<string[]> {
    return this.crm.getContactStatuses()
  }

  async getContactSources(): Promise<string[]> {
    return this.crm.getContactSources()
  }

  async getContactPipelines(): Promise<string[]> {
    return this.crm.getContactPipelines()
  }

  async linkContactToUser(contactId: number, userId: number): Promise<Contact> {
    return this.crm.linkContactToUser(contactId, userId)
  }

  async linkExistingUserToContactAdmin(contactId: number, email: string): Promise<Contact> {
    return this.crm.linkExistingUserToContactAdmin(contactId, email)
  }

  async createPortalAccountAdmin(contactId: number, email: string): Promise<Contact> {
    return this.crm.createPortalAccountAdmin(contactId, email)
  }

  // Contact Notes
  async getContactNotesAdmin(contactId: number, params?: FilterParams): Promise<PaginatedResponse<ContactNote>> {
    return this.crm.getContactNotesAdmin(contactId, params)
  }

  async createContactNoteAdmin(data: { contact_id: number; text: string }): Promise<ContactNote> {
    return this.crm.createContactNoteAdmin(data)
  }

  async updateContactNoteAdmin(contactId: number, noteId: number, text: string): Promise<ContactNote> {
    return this.crm.updateContactNoteAdmin(contactId, noteId, text)
  }

  async deleteContactNoteAdmin(contactId: number, noteId: number): Promise<void> {
    return this.crm.deleteContactNoteAdmin(contactId, noteId)
  }

  async getContactNotes(contactId: number): Promise<ContactNote[]> {
    return this.crm.getContactNotes(contactId)
  }

  async createContactNote(contactId: number, data: ContactNoteCreateInput): Promise<ContactNote> {
    return this.crm.createContactNote(contactId, data)
  }

  async updateContactNote(contactId: number, noteId: number, data: Partial<ContactNoteCreateInput>): Promise<ContactNote> {
    return this.crm.updateContactNote(contactId, noteId, data)
  }

  async deleteContactNote(contactId: number, noteId: number): Promise<void> {
    return this.crm.deleteContactNote(contactId, noteId)
  }

  // Contact Business
  async getContactBusinesses(): Promise<ContactBusiness[]> {
    return this.crm.getContactBusinesses()
  }

  async createContactBusiness(data: ContactBusinessCreateInput): Promise<ContactBusiness> {
    return this.crm.createContactBusiness(data)
  }

  async updateContactBusiness(id: number, data: ContactBusinessUpdateInput): Promise<ContactBusiness> {
    return this.crm.updateContactBusiness(id, data)
  }

  async deleteContactBusiness(id: number): Promise<void> {
    return this.crm.deleteContactBusiness(id)
  }

  // Contact Submissions
  async getContactSubmissions(params?: FilterParams): Promise<PaginatedResponse<ContactSubmission>> {
    return this.crm.getContactSubmissions(params)
  }

  async submitPublicContactForm(data: {
    name: string
    email: string
    request?: string
    phone?: string
    company?: string
    comments?: string
    selected_products?: Array<{
      product_id: number
      product_name: string
      quantity: number
      unit: string
    }>
    metadata?: Record<string, any>
  }): Promise<ContactSubmission> {
    return this.crm.submitPublicContactForm(data)
  }

  async getContactSubmission(uuid: string): Promise<ContactSubmission> {
    return this.crm.getContactSubmission(uuid)
  }

  async updateContactSubmission(uuid: string, data: ContactSubmissionUpdateInput): Promise<ContactSubmission> {
    return this.crm.updateContactSubmission(uuid, data)
  }

  async deleteContactSubmission(uuid: string): Promise<void> {
    return this.crm.deleteContactSubmission(uuid)
  }

  async bulkUpdateContactSubmissionStatus(uuids: string[], status: string): Promise<void> {
    return this.crm.bulkUpdateContactSubmissionStatus(uuids, status)
  }

  async bulkAssignContactSubmissions(uuids: string[], assignedTo: number): Promise<void> {
    return this.crm.bulkAssignContactSubmissions(uuids, assignedTo)
  }

  async markContactSubmissionResponded(uuid: string): Promise<ContactSubmission> {
    return this.crm.markContactSubmissionResponded(uuid)
  }

  async getContactSubmissionStats(): Promise<{
    total: number
    unread: number
    responded: number
    by_status: Record<string, number>
  }> {
    return this.crm.getContactSubmissionStats()
  }

  // Admin Contact Submissions
  async getContactSubmissionsAdmin(params?: FilterParams): Promise<PaginatedResponse<ContactSubmission>> {
    return this.crm.getContactSubmissionsAdmin(params)
  }

  async getContactSubmissionAdmin(id: number): Promise<ContactSubmission> {
    return this.crm.getContactSubmissionAdmin(id)
  }

  async updateContactSubmissionAdmin(id: number, data: ContactSubmissionUpdateInput): Promise<ContactSubmission> {
    return this.crm.updateContactSubmissionAdmin(id, data)
  }

  async deleteContactSubmissionAdmin(id: number): Promise<void> {
    return this.crm.deleteContactSubmissionAdmin(id)
  }

  async bulkUpdateContactSubmissionStatusAdmin(submissionIds: number[], status: string): Promise<{ updated_count: number }> {
    return this.crm.bulkUpdateContactSubmissionStatusAdmin(submissionIds, status)
  }

  async bulkAssignContactSubmissionsAdmin(submissionIds: number[], assignedToId: number | null): Promise<{ updated_count: number }> {
    return this.crm.bulkAssignContactSubmissionsAdmin(submissionIds, assignedToId)
  }

  async markContactSubmissionRespondedAdmin(id: number): Promise<ContactSubmission> {
    return this.crm.markContactSubmissionRespondedAdmin(id)
  }

  async getContactSubmissionStatsAdmin(): Promise<{
    total: number
    by_status: Record<string, number>
    by_source: Record<string, number>
    recent_count: number
    response_rate: number
  }> {
    return this.crm.getContactSubmissionStatsAdmin()
  }

  // Enhanced contact methods
  async getCustomerContacts(params?: { q?: string; page?: number }): Promise<PaginatedResponse<Contact>> {
    return this.crm.getCustomerContacts(params)
  }

  async getContactAddresses(contactId: number): Promise<Address[]> {
    return this.crm.getContactAddresses(contactId)
  }

  async createContactAddress(contactId: number, data: any): Promise<Address> {
    return this.crm.createContactAddress(contactId, data)
  }

  async updateContactAddress(contactId: number, addressId: number, data: any): Promise<Address> {
    return this.crm.updateContactAddress(contactId, addressId, data)
  }

  async deleteContactAddress(contactId: number, addressId: number): Promise<void> {
    return this.crm.deleteContactAddress(contactId, addressId)
  }

  async getActivityFeed(params?: { limit?: number; types?: string }): Promise<Array<{
    id: number
    type: 'order' | 'note' | 'contact'
    title: string
    description: string
    timestamp: string
    user?: string
    entity_id?: number
  }>> {
    return this.crm.getActivityFeed(params)
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY METHODS - ORDERS
  // ============================================================================

  async getOrders(params?: FilterParams): Promise<PaginatedResponse<Order>> {
    return this.orders.getOrders(params)
  }

  async getOrder(id: number): Promise<Order> {
    return this.orders.getOrder(id)
  }

  async getCRMOrders(params?: FilterParams): Promise<PaginatedResponse<Order>> {
    return this.orders.getCRMOrders(params)
  }

  async getCRMOrder(id: number): Promise<Order> {
    return this.orders.getCRMOrder(id)
  }

  async updateCRMOrder(id: number, data: Partial<Order>): Promise<Order> {
    return this.orders.updateCRMOrder(id, data)
  }

  async updateCRMOrderStatus(id: number, status: string): Promise<Order> {
    return this.orders.updateCRMOrderStatus(id, status)
  }

  async createOrder(data: OrderCreateInput): Promise<Order> {
    return this.orders.createOrder(data)
  }

  async updateOrder(id: number, data: OrderUpdateInput): Promise<Order> {
    return this.orders.updateOrder(id, data)
  }

  async updateOrderStatus(id: number, status: string): Promise<Order> {
    return this.orders.updateOrderStatus(id, status)
  }

  async updateOrderTracking(id: number, trackingNumber: string, carrier: string): Promise<Order> {
    return this.orders.updateOrderTracking(id, trackingNumber, carrier)
  }

  async addOrderNote(id: number, note: string): Promise<Order> {
    return this.orders.addOrderNote(id, note)
  }

  async bulkUpdateOrderStatus(ids: number[], status: string): Promise<void> {
    return this.orders.bulkUpdateOrderStatus(ids, status)
  }

  async cancelOrder(id: number): Promise<Order> {
    return this.orders.cancelOrder(id)
  }

  async completeOrder(id: number): Promise<Order> {
    return this.orders.completeOrder(id)
  }

  async approveOrder(id: number): Promise<{
    success: boolean
    order_id: number
    status: string
    message: string
    invoice_created?: boolean
    invoice_id?: string
    invoice_url?: string
  }> {
    return this.orders.approveOrder(id)
  }

  async getPaymentMethods(): Promise<Array<{id: number, name: string, process_at_checkout: boolean}>> {
    return this.orders.getPaymentMethods()
  }

  async getShippingMethods(): Promise<Array<{id: number, size: string, weight: number, speed: string}>> {
    return this.orders.getShippingMethods()
  }

  async getOrderCreationHelper(): Promise<{
    customer_id: number
    payment_methods: Array<{id: number, name: string, process_at_checkout: boolean}>
    shipping_methods: Array<{id: number, size: string, weight: number, speed: string}>
    saved_addresses: Array<{id: number, address: string, city: string, state: string, zipcode: string, name: string, phone: string}>
    example_payload: any
  }> {
    return this.orders.getOrderCreationHelper()
  }

  async getUserOrders(params?: FilterParams): Promise<PaginatedResponse<Order>> {
    return this.orders.getUserOrders(params)
  }

  // Admin order methods
  async getOrdersAdmin(params?: FilterParams): Promise<PaginatedResponse<Order>> {
    return this.orders.getOrdersAdmin(params)
  }

  async getOrderAdmin(id: number): Promise<Order> {
    return this.orders.getOrderAdmin(id)
  }

  async createOrderAdmin(data: OrderCreateInput): Promise<Order> {
    return this.orders.createOrderAdmin(data)
  }

  async updateOrderAdmin(id: number, data: OrderUpdateInput): Promise<Order> {
    return this.orders.updateOrderAdmin(id, data)
  }

  async updateOrderStatusAdmin(id: number, status: string): Promise<Order> {
    return this.orders.updateOrderStatusAdmin(id, status)
  }

  async updateOrderTrackingAdmin(id: number, trackingNumber: string): Promise<Order> {
    return this.orders.updateOrderTrackingAdmin(id, trackingNumber)
  }

  async addOrderNoteAdmin(id: number, text: string): Promise<Order> {
    return this.orders.addOrderNoteAdmin(id, text)
  }

  async cancelOrderAdmin(id: number, reason?: string): Promise<Order> {
    return this.orders.cancelOrderAdmin(id, reason)
  }

  async completeOrderAdmin(id: number): Promise<Order> {
    return this.orders.completeOrderAdmin(id)
  }

  async getPaymentMethodsAdmin(): Promise<Array<{id: number, name: string}>> {
    return this.orders.getPaymentMethodsAdmin()
  }

  async getShippingMethodsAdmin(): Promise<Array<{id: number, name: string}>> {
    return this.orders.getShippingMethodsAdmin()
  }

  // Address Management - Uses order address endpoints (where addresses are actually stored)
  async getAddresses(params?: any): Promise<PaginatedResponse<Address>> {
    return this.orders.getAddresses(params)
  }

  async getAddress(id: number): Promise<Address> {
    return this.orders.getAddress(id)
  }

  async createAddress(data: AddressCreateInput): Promise<Address> {
    return this.orders.createAddress(data)
  }

  async updateAddress(id: number, data: AddressUpdateInput): Promise<Address> {
    return this.orders.updateAddress(id, data)
  }

  async deleteAddress(id: number): Promise<void> {
    return this.orders.deleteAddress(id)
  }

  async setDefaultAddress(id: number): Promise<Address> {
    return this.orders.setDefaultAddress(id)
  }

  async archiveAddress(id: number): Promise<{ message: string; archived_at: string; address_id: number }> {
    return this.orders.archiveAddress(id)
  }

  async restoreAddress(id: number): Promise<Address> {
    return this.orders.restoreAddress(id)
  }

  async getUserAddresses(params?: any): Promise<PaginatedResponse<Address>> {
    return this.orders.getUserAddresses(params)
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY METHODS - AUTHENTICATION & PORTAL
  // ============================================================================

  async checkUserExists(email: string): Promise<{ exists: boolean; user?: User }> {
    return this.auth.checkUserExists(email)
  }

  async createPortalAccount(contactId: number, email: string): Promise<{ success: boolean; user: User }> {
    return this.auth.createPortalAccount(contactId, email)
  }

  async sendPortalInvite(contactId: number, email?: string): Promise<{ 
    success: boolean
    message: string
    invite_status?: 'sent' | 'resent' | 'already_accepted'
    user?: User 
  }> {
    return this.auth.sendPortalInvite(contactId, email)
  }

  async resendPortalInvite(contactId: number): Promise<{ 
    success: boolean
    message: string
    invite_status: 'resent'
  }> {
    return this.auth.resendPortalInvite(contactId)
  }

  async getPortalInviteStatus(contactId: number): Promise<{
    has_account: boolean
    invite_sent: boolean
    invite_accepted: boolean
    user_email?: string
    invite_sent_at?: string
    invite_accepted_at?: string
  }> {
    return this.auth.getPortalInviteStatus(contactId)
  }

  async linkExistingUserToContact(contactId: number, userId: number): Promise<Contact> {
    return this.auth.linkExistingUserToContact(contactId, userId)
  }

  async unlinkContactFromUser(contactId: number): Promise<Contact> {
    return this.auth.unlinkContactFromUser(contactId)
  }

  async getUserProfile(): Promise<UserProfile> {
    return this.auth.getUserProfile()
  }

  // Password Management
  async changePassword(data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<{ success: true; message: string; timestamp: string }> {
    return this.auth.changePassword(data)
  }

  async checkPasswordStrength(password: string) {
    return this.auth.checkPasswordStrength(password)
  }

  async getSecuritySettings() {
    return this.auth.getSecuritySettings()
  }

  async updateUserProfile(data: ProfileUpdateInput): Promise<UserProfile> {
    return this.auth.updateUserProfile(data)
  }

  async getNotificationPreferences(contactId?: string): Promise<NotificationPreferences> {
    return this.auth.getNotificationPreferences(contactId)
  }

  async updateNotificationPreferences(data: NotificationPreferencesUpdateInput, contactId?: string): Promise<NotificationPreferences> {
    return this.auth.updateNotificationPreferences(data, contactId)
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY METHODS - USERS
  // ============================================================================

  async getUsers(params?: FilterParams): Promise<PaginatedResponse<User>> {
    return this.users.getUsers(params)
  }

  async getUser(id: number): Promise<User> {
    return this.users.getUser(id)
  }

  async createUser(data: UserCreateInput): Promise<User> {
    return this.users.createUser(data)
  }

  async updateUser(id: number, data: UserUpdateInput): Promise<User> {
    return this.users.updateUser(id, data)
  }

  async deleteUser(id: number): Promise<void> {
    return this.users.deleteUser(id)
  }

  async getUserRoles(): Promise<Array<{ id: number; name: string; permissions: string[] }>> {
    return this.users.getUserRoles()
  }

  async assignUserRoles(userId: number, roleIds: number[]): Promise<User> {
    return this.users.assignUserRoles(userId, roleIds)
  }

  // Admin Users
  async getAdminUsers(params?: FilterParams): Promise<PaginatedResponse<User>> {
    return this.users.getAdminUsers(params)
  }

  async getAdminUser(id: number): Promise<User> {
    return this.users.getAdminUser(id)
  }

  async createAdminUser(data: UserCreateInput): Promise<User> {
    return this.users.createAdminUser(data)
  }

  async updateAdminUser(id: number, data: UserUpdateInput): Promise<User> {
    return this.users.updateAdminUser(id, data)
  }

  async deleteAdminUser(id: number): Promise<void> {
    return this.users.deleteAdminUser(id)
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY METHODS - ANALYTICS & DASHBOARD
  // ============================================================================

  async getDashboardStats(): Promise<AdminDashboardStats> {
    return this.analytics.getDashboardStats()
  }

  async getDashboardInventoryAlerts(): Promise<{
    low_stock: InventoryItem[]
    out_of_stock: InventoryItem[]
    overstocked: InventoryItem[]
  }> {
    return this.analytics.getDashboardInventoryAlerts()
  }

  async getOrderStats(period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<{
    total_orders: number
    total_revenue: number
    average_order_value: number
    by_status: Record<string, number>
  }> {
    return this.analytics.getOrderStats(period)
  }

  async getOrderStatsAdmin(dateFrom?: string, dateTo?: string): Promise<{
    total_orders: number
    total_revenue: number
    orders_by_status: Record<string, number>
    revenue_by_day: Array<{ date: string; revenue: number }>
  }> {
    return this.analytics.getOrderStatsAdmin(dateFrom, dateTo)
  }

  async getOrderStatusDistribution(): Promise<{
    status_counts: Record<string, number>
    status_values: Record<string, number>
    total_orders: number
    total_value: number
  }> {
    return this.analytics.getOrderStatusDistribution()
  }

  async getRevenueAnalytics(params?: { 
    period?: 'daily' | 'weekly' | 'monthly'
    start_date?: string
    end_date?: string 
  }): Promise<{
    labels: string[]
    revenue_data: number[]
    order_count_data: number[]
    total_revenue: number
    total_orders: number
    period: string
  }> {
    return this.analytics.getRevenueAnalytics(params)
  }

  async getTopCustomers(limit: number = 10): Promise<Array<{
    contact: Contact
    total_orders: number
    total_spent: number
  }>> {
    return this.analytics.getTopCustomers(limit)
  }

  async getRecentOrders(limit: number = 10): Promise<Order[]> {
    return this.analytics.getRecentOrders(limit)
  }

  async getRecentOrdersAdmin(limit: number = 10): Promise<Order[]> {
    return this.analytics.getRecentOrdersAdmin(limit)
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY METHODS - SYSTEM & UTILITIES
  // ============================================================================

  async getSystemHealth(): Promise<{
    status: 'healthy' | 'degraded' | 'down'
    database: boolean
    cache: boolean
    storage: boolean
    api_latency: number
  }> {
    return this.system.getSystemHealth()
  }

  async getSystemHealthAdmin(): Promise<{
    status: 'healthy' | 'warning' | 'error'
    checks: Array<{
      name: string
      status: 'pass' | 'fail' | 'warning'
      message?: string
    }>
  }> {
    return this.system.getSystemHealthAdmin()
  }

  async getAuditLogs(params?: {
    user_id?: number
    entity_type?: string
    entity_id?: number
    action?: string
    start_date?: string
    end_date?: string
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<{
    id: number
    user: User
    action: string
    entity_type: string
    entity_id: number
    changes: Record<string, any>
    created_at: string
  }>> {
    return this.system.getAuditLogs(params)
  }

  async getAuditLogsAdmin(params?: {
    page?: number
    limit?: number
    user_id?: number
    action?: string
    date_from?: string
    date_to?: string
  }): Promise<PaginatedResponse<{
    id: number
    user: User
    action: string
    model: string
    object_id: string
    changes: Record<string, any>
    timestamp: string
  }>> {
    return this.system.getAuditLogsAdmin(params)
  }

  async exportData(
    entityType: 'contacts' | 'products' | 'orders',
    format: 'csv' | 'json' | 'xlsx' = 'csv'
  ): Promise<Blob> {
    return this.system.exportData(entityType, format)
  }

  async exportDataAdmin(type: 'contacts' | 'orders' | 'inventory', format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    return this.system.exportDataAdmin(type, format)
  }

  // Utility methods
  isStaffUser(response: any): boolean {
    return this.system.isStaffUser(response)
  }

  detectAvailableFields(data: any[]): Set<string> {
    return this.system.detectAvailableFields(data)
  }

  // ============================================================================
  // BATCH OPERATIONS (ADMIN-SPECIFIC)
  // ============================================================================

  // ============================================================================
  // NEW API METHODS FROM SYNC
  // ============================================================================

  // Order Timeline & History
  async getOrderTimeline(id: number): Promise<OrderTimeline> {
    return this.orders.getOrderTimeline(id)
  }

  async getOrderHistory(id: number): Promise<OrderHistory> {
    return this.orders.getOrderHistory(id)
  }

  async getOrderInvoicePDF(id: number): Promise<Blob> {
    return this.orders.getOrderInvoicePDF(id)
  }

  // Contact Interaction History
  async getContactInteractionHistory(
    contactId: number,
    params?: {
      start_date?: string
      end_date?: string
      interaction_type?: 'order' | 'email' | 'phone' | 'meeting' | 'note'
      limit?: number
    }
  ): Promise<ContactInteractionHistory> {
    return this.crm.getContactInteractionHistory(contactId, params)
  }

  async getContactNotificationPreferences(contactId: number): Promise<ContactNotificationPreferences> {
    return this.crm.getContactNotificationPreferences(contactId)
  }

  async updateContactNotificationPreferences(
    contactId: number,
    data: Partial<ContactNotificationPreferences>
  ): Promise<ContactNotificationPreferences> {
    return this.crm.updateContactNotificationPreferences(contactId, data)
  }

  async assignContactSubmission(uuid: string, contactId: number): Promise<ContactSubmission> {
    return this.crm.assignContactSubmission(uuid, contactId)
  }

  // Advanced Inventory Features
  async advancedProductSearch(searchData: {
    query?: string
    filters?: {
      category?: string
      min_price?: number
      max_price?: number
      in_stock?: boolean
    }
    sort_by?: string
    page?: number
    per_page?: number
  }): Promise<PaginatedResponse<InventoryItem>> {
    return this.inventory.advancedProductSearch(searchData)
  }

  async getProductRecommendations(data: {
    product_id?: number
    customer_id?: number
    recommendation_type: 'similar' | 'frequently_bought_together' | 'trending' | 'personalized'
    limit?: number
  }): Promise<ProductRecommendation[]> {
    return this.inventory.getProductRecommendations(data)
  }

  async getBatchProductActivityStream(params?: {
    start_date?: string
    end_date?: string
    product_id?: number
    page?: number
    per_page?: number
  }): Promise<PaginatedResponse<BatchActivity>> {
    return this.inventory.getBatchProductActivityStream(params)
  }

  async getProductionRunsDashboard(params?: {
    date_from?: string
    date_to?: string
  }): Promise<ProductionDashboard> {
    return this.inventory.getProductionRunsDashboard(params)
  }

  async getMaterialTransfers(params?: {
    page?: number
  }): Promise<PaginatedResponse<MaterialTransfer>> {
    return this.inventory.getMaterialTransfers(params)
  }

  async getMaterialTransferTracking(transferId: string): Promise<MaterialTransferTracking> {
    return this.inventory.getMaterialTransferTracking(transferId)
  }

  async updateMaterialTransferTracking(
    transferId: string,
    data: Partial<MaterialTransferTracking>
  ): Promise<MaterialTransferTracking> {
    return this.inventory.updateMaterialTransferTracking(transferId, data)
  }

  // Analytics
  async getDashboardSummary(): Promise<DashboardSummary> {
    return this.analytics.getDashboardSummary()
  }

  async bulkUpdateContactStatusAdmin(contactIds: number[], statusId: number): Promise<{ updated_count: number }> {
    return this.crm.bulkUpdateContactStatusAdmin(contactIds, statusId)
  }

  async bulkUpdateOrderStatusAdmin(orderIds: number[], status: string): Promise<{ updated_count: number }> {
    return this.orders.bulkUpdateOrderStatusAdmin(orderIds, status)
  }

  // ============================================================================
  // BACKWARD COMPATIBILITY METHODS - ACTIVITIES
  // ============================================================================

  async getContactActivities(
    contactId: number,
    filters?: {
      activity_type?: string
      start_date?: string
      end_date?: string
      limit?: number
    },
    useCache = true
  ): Promise<import('./activities').ContactActivitiesResponse> {
    return this.activities.getContactActivities(contactId, filters || {}, useCache)
  }

  async getOrderActivities(
    orderId: number,
    filters?: {
      activity_type?: string
      start_date?: string
      end_date?: string
      limit?: number
    },
    useCache = true
  ): Promise<import('./activities').OrderActivitiesResponse> {
    return this.activities.getOrderActivities(orderId, filters || {}, useCache)
  }

  async getUnifiedActivityFeed(
    filters?: {
      source?: 'contact' | 'order' | 'all'
      activity_type?: string
      user?: string
      start_date?: string
      end_date?: string
      limit?: number
    },
    useCache = true
  ): Promise<import('./activities').UnifiedActivityFeedResponse> {
    return this.activities.getUnifiedActivityFeed(filters || {}, useCache)
  }

  async getRecentActivities(limit = 20): Promise<import('./activities').UnifiedActivity[]> {
    return this.activities.getRecentActivities(limit)
  }

  async getUserActivities(user: string, limit = 50): Promise<import('./activities').UnifiedActivity[]> {
    return this.activities.getUserActivities(user, limit)
  }

  async getActivitiesByType(
    activityType: import('./activities').ActivityType,
    limit = 50
  ): Promise<import('./activities').UnifiedActivity[]> {
    return this.activities.getActivitiesByType(activityType, limit)
  }

  async getActivitiesInDateRange(
    startDate: string,
    endDate: string,
    limit = 100
  ): Promise<import('./activities').UnifiedActivity[]> {
    return this.activities.getActivitiesInDateRange(startDate, endDate, limit)
  }

  async getAvailableActivityFilters(): Promise<import('./activities').AvailableFilters> {
    return this.activities.getAvailableFilters()
  }

  async getActivitiesSince(
    sinceTimestamp: string,
    source: 'contact' | 'order' | 'all' = 'all'
  ): Promise<import('./activities').UnifiedActivity[]> {
    return this.activities.getActivitiesSince(sinceTimestamp, source)
  }

  // Activity cache management methods
  clearActivityCache(pattern?: string): void {
    return this.activities.clearCache(pattern)
  }

  getActivityCacheStats(): { size: number; entries: string[] } {
    return this.activities.getCacheStats()
  }

  invalidateContactActivityCache(contactId: number): void {
    return this.activities.invalidateContactCache(contactId)
  }

  invalidateOrderActivityCache(orderId: number): void {
    return this.activities.invalidateOrderCache(orderId)
  }

  // ============================================================================
  // QUICKBOOKS INTEGRATION METHODS (delegate to QuickBooks module)
  // ============================================================================

  async getQuickBooksConnectionStatus() {
    return this.quickbooks.getConnectionStatus()
  }

  async initiateQuickBooksOAuth() {
    return this.quickbooks.initiateOAuth()
  }

  async syncOrderToQuickBooks(orderId: number) {
    return this.quickbooks.syncOrderToQuickBooks(orderId)
  }

  async disconnectQuickBooks() {
    return this.quickbooks.disconnect()
  }

  // ============================================================================
  // HARVEST OPERATIONS METHODS
  // ============================================================================

  async getHarvestPlans(params?: FilterParams & { farm?: number }): Promise<PaginatedResponse<HarvestPlan>> {
    return this.harvest.getHarvestPlans(params)
  }

  async createHarvestPlan(data: HarvestPlanCreateInput): Promise<HarvestPlan> {
    return this.harvest.createHarvestPlan(data)
  }

  async getHarvestPlan(id: number): Promise<HarvestPlan> {
    return this.harvest.getHarvestPlan(id)
  }

  async updateHarvestPlan(id: number, data: HarvestPlanUpdateInput): Promise<HarvestPlan> {
    return this.harvest.updateHarvestPlan(id, data)
  }

  async getHarvestLocations(): Promise<Array<{ id: number; name: string }>> {
    return this.harvest.getHarvestLocations()
  }

  async deleteHarvestPlan(id: number): Promise<void> {
    return this.harvest.deleteHarvestPlan(id)
  }

  async getHarvestPlanContainers(harvestPlanId: number): Promise<Container[]> {
    return this.harvest.getHarvestPlanContainers(harvestPlanId)
  }

  async getContainers(params?: FilterParams & { harvest_plan?: number, is_rnd?: boolean }): Promise<PaginatedResponse<Container>> {
    return this.harvest.getContainers(params)
  }

  async createContainer(data: ContainerCreateInput): Promise<Container> {
    return this.harvest.createContainer(data)
  }

  async getContainer(id: number): Promise<Container> {
    return this.harvest.getContainer(id)
  }

  async updateContainer(id: number, data: any): Promise<Container> {
    return this.harvest.updateContainer(id, data)
  }

  async getContainerPallets(containerId: number): Promise<Pallet[]> {
    return this.harvest.getContainerPallets(containerId)
  }

  async getPallets(params?: FilterParams & { container?: number, location?: number, status?: string }): Promise<PaginatedResponse<Pallet>> {
    return this.harvest.getPallets(params)
  }

  async createPallet(data: PalletCreateInput): Promise<Pallet> {
    return this.harvest.createPallet(data)
  }

  async getPallet(id: number): Promise<Pallet> {
    return this.harvest.getPallet(id)
  }

  async updatePallet(id: number, data: PalletUpdateInput): Promise<Pallet> {
    return this.harvest.updatePallet(id, data)
  }

  async generatePalletQRCode(palletId: number): Promise<PalletQRCodeResponse> {
    return this.harvest.generatePalletQRCode(palletId)
  }

  async scanQRCode(scanData: PalletScanRequest): Promise<Pallet> {
    return this.harvest.scanQRCode(scanData)
  }

  async generatePalletLabel(palletId: number, format: 'pdf' | 'html' = 'html'): Promise<string | ArrayBuffer> {
    return this.harvest.generatePalletLabel(palletId, format)
  }

  async generateBulkLabels(data: BulkLabelRequest): Promise<string | ArrayBuffer> {
    return this.harvest.generateBulkLabels(data)
  }

  async createMaterialTransfer(data: MaterialTransferCreateInput): Promise<MaterialTransfer> {
    return this.harvest.createMaterialTransfer(data)
  }

  async getHarvestDashboardStats(): Promise<{
    active_plans: number;
    total_containers: number;
    total_pallets: number;
    recent_activity: Array<{ id: string; type: string; title: string; description: string; timestamp: string; }>;
  }> {
    return this.harvest.getDashboardStats()
  }

  async getFarms(): Promise<Array<{ id: number; name: string }>> {
    return this.harvest.getFarms()
  }

  async getLocations(): Promise<Array<{ id: number; name: string }>> {
    return this.harvest.getLocations()
  }

  // ============================================================================
  // LEGACY HTTP METHODS (for backward compatibility)
  // ============================================================================

  private async get<T>(url: string, params?: any): Promise<T> {
    return this.makeRequest<T>(url, { params })
  }

  private async post<T>(url: string, data?: any): Promise<T> {
    console.log('[SiloAPI] POST request to:', url);
    console.log('[SiloAPI] POST data:', JSON.stringify(data, null, 2));
    
    return this.makeRequest<T>(url, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  private async patch<T>(url: string, data?: any): Promise<T> {
    return this.makeRequest<T>(url, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  private async delete<T = void>(url: string): Promise<T> {
    return this.makeRequest<T>(url, { method: 'DELETE' })
  }

  private async put<T>(url: string, data?: any): Promise<T> {
    return this.makeRequest<T>(url, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }
}

// Export singleton instance
export const siloApi = new SiloApiClient()

// Re-export types and errors for convenience
export * from './types'
export * from './errors'
export * from './activities'
export * from './quickbooks'
export { CrmApi, InventoryApi, OrdersApi, AuthApi, UsersApi, AnalyticsApi, SystemApi, ActivitiesApi, QuickBooksApi, HarvestApi }